import React, { createContext, useContext, useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import UserDashboard from "@/pages/user/UserDashboard";
import UserWallet from "@/pages/user/UserWallet";
import UserReferrals from "@/pages/user/UserReferrals";
import UserProfile from "@/pages/user/UserProfile";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import ReelsPage from "@/pages/ReelsPage";
import BrandsPage from "@/pages/BrandsPage";
import FinancePage from "@/pages/FinancePage";
import SellerDashboard from "@/pages/seller/SellerDashboard";
import SellerProducts from "@/pages/seller/SellerProducts";
import SellerReels from "@/pages/seller/SellerReels";
import SellerRegister from "@/pages/seller/SellerRegister";
import SellerStoreSettings from "@/pages/seller/SellerStoreSettings";
import SellerAnalytics from "@/pages/seller/SellerAnalytics";
import SellerPlans from "@/pages/seller/SellerPlans";
import { MobileBottomNav } from "@/components/site/MobileBottomNav";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSellers from "@/pages/admin/AdminSellers";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminWithdrawals from "@/pages/admin/AdminWithdrawals";
import AdminMLM from "@/pages/admin/AdminMLM";
import AdminContent from "@/pages/admin/AdminContent";
import AdminReels from "@/pages/admin/AdminReels";
import AdminKYC from "@/pages/admin/AdminKYC";
import SellerKYC from "@/pages/seller/SellerKYC";
import AdminBrands from "@/pages/admin/AdminBrands";
import AdminFinance from "@/pages/admin/AdminFinance";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminApplications from "@/pages/admin/AdminApplications";
import StorePage from "@/pages/StorePage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("token");
    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
      setToken(storedToken);
    }
    setLoading(false);
  }, []);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token", authToken);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`,
  });

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedTypes }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedTypes && !allowedTypes.includes(user.user_type)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Initialize API data
const initializeApp = async () => {
  try {
    await axios.post(`${API}/init`);
  } catch (error) {
    console.error("Failed to initialize app:", error);
  }
};

function App() {
  useEffect(() => {
    initializeApp();
  }, []);

  return (
    <AuthProvider>
      <div className="App min-h-screen bg-gray-50">
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <MobileBottomNav />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/seller/register" element={<SellerRegister />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/:category" element={<ProductsPage />} />
            <Route path="/product/:productId" element={<ProductDetailPage />} />
            <Route path="/store/:storeSlug" element={<StorePage />} />
            <Route path="/store/:storeSlug/p/:pageSlug" element={<StorePage />} />

            {/* User Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedTypes={["user"]}>
                  <UserDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wallet"
              element={
                <ProtectedRoute allowedTypes={["user"]}>
                  <UserWallet />
                </ProtectedRoute>
              }
            />
            <Route
              path="/referrals"
              element={
                <ProtectedRoute allowedTypes={["user"]}>
                  <UserReferrals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute allowedTypes={["user"]}>
                  <UserProfile />
                </ProtectedRoute>
              }
            />

            {/* Reels - Public Route */}
            <Route path="/reels" element={<ReelsPage />} />

            {/* Brands - Public Route */}
            <Route path="/brands" element={<BrandsPage />} />
            <Route path="/brands/:brandId" element={<BrandsPage />} />

            {/* Finance - Public Route */}
            <Route path="/finance" element={<FinancePage />} />

            {/* Admin Login - Separate Route (hidden from public navigation) */}
            <Route path="/admin-login" element={<AdminLoginPage />} />

            {/* Seller Routes */}
            <Route
              path="/seller/dashboard"
              element={
                <ProtectedRoute allowedTypes={["seller"]}>
                  <SellerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seller/products"
              element={
                <ProtectedRoute allowedTypes={["seller"]}>
                  <SellerProducts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seller/reels"
              element={
                <ProtectedRoute allowedTypes={["seller"]}>
                  <SellerReels />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seller/kyc"
              element={
                <ProtectedRoute allowedTypes={["seller"]}>
                  <SellerKYC />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seller/store-settings"
              element={
                <ProtectedRoute allowedTypes={["seller"]}>
                  <SellerStoreSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seller/plans"
              element={
                <ProtectedRoute allowedTypes={["seller"]}>
                  <SellerPlans />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seller/analytics"
              element={
                <ProtectedRoute allowedTypes={["seller"]}>
                  <SellerAnalytics />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedTypes={["admin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedTypes={["admin"]}>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/sellers"
              element={
                <ProtectedRoute allowedTypes={["admin"]}>
                  <AdminSellers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/products"
              element={
                <ProtectedRoute allowedTypes={["admin"]}>
                  <AdminProducts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/withdrawals"
              element={
                <ProtectedRoute allowedTypes={["admin"]}>
                  <AdminWithdrawals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/mlm"
              element={
                <ProtectedRoute allowedTypes={["admin"]}>
                  <AdminMLM />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/content"
              element={
                <ProtectedRoute allowedTypes={["admin"]}>
                  <AdminContent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reels"
              element={
                <ProtectedRoute allowedTypes={["admin"]}>
                  <AdminReels />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/brands"
              element={
                <ProtectedRoute allowedTypes={["admin"]}>
                  <AdminBrands />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/finance"
              element={
                <ProtectedRoute allowedTypes={["admin"]}>
                  <AdminFinance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute allowedTypes={["admin"]}>
                  <AdminAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/kyc"
              element={
                <ProtectedRoute allowedTypes={["admin"]}>
                  <AdminKYC />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/applications"
              element={
                <ProtectedRoute allowedTypes={["admin"]}>
                  <AdminApplications />
                </ProtectedRoute>
              }
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
